import { useMemo } from 'react';

import OptimizationPipelineConfigurableTable from '../OptimizationPipelineConfigurableTable';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

export default function UnitDetailsSidebar() {
    const { cleanMeasurementDataList, mergedMeasurementDataList, ts, cleanMeasurementData } = useOptimizationPipeline();

    function isPrimitive(x) { return x == null || typeof x !== 'object' || x instanceof Date; }

    function getEntryByTs(list) {
        if (!Array.isArray(list) || !ts) return null;
        const target = (ts instanceof Date) ? ts.getTime() : new Date(ts).getTime();
        if (Number.isNaN(target)) return null;
        return list.find(e => { const t = e?.ts ? new Date(e.ts).getTime() : NaN; return !Number.isNaN(t) && t === target; }) || null;
    }

    const uomMap = useMemo(() => {
        const m = new Map();
        const fields = cleanMeasurementData?.fields || [];
        fields.forEach(f => { if (f?.name) m.set(f.name, f?.uom ?? null); });
        return m;
    }, [cleanMeasurementData]);

    function getUomForPath(path) {
        if (!path) return null;
        const root = String(path).split(/[.\[]/)[0];
        return uomMap.get(root) ?? null;
    }

    function buildDataFromEntry(entry) {
        const out = {};
        if (!entry || typeof entry !== 'object') return out;

        const flatten = (prefix, val) => {
            if (isPrimitive(val)) {
                const u = getUomForPath(prefix);
                out[prefix] = u ? { value: val, uom: u } : { value: val };
                return;
            }
            if (Array.isArray(val)) {
                val.forEach((item, i) => flatten(`${prefix}[${i}]`, item));
                return;
            }
            const keys = Object.keys(val || {});
            if (keys.length === 0) {
                const u = getUomForPath(prefix);
                out[prefix] = u ? { value: null, uom: u } : { value: null };
                return;
            }
            for (const k of keys) {
                const next = prefix ? `${prefix}.${k}` : k;
                flatten(next, val[k]);
            }
        };

        for (const [k, v] of Object.entries(entry)) {
            if (k === 'id' || k === 'ts' || k === 'meta') continue;
            if (isPrimitive(v)) {
                const u = getUomForPath(k);
                out[k] = u ? { value: v, uom: u } : { value: v };
            } else {
                flatten(k, v);
            }
        }
        return out;
    }

    const data = useMemo(() => {
        // console.log(mergedMeasurementDataList);
        const entry = getEntryByTs(mergedMeasurementDataList, ts);
        return buildDataFromEntry(entry);
    }, [mergedMeasurementDataList, ts, uomMap]);

    const title = useMemo(() => {
        const label = ts ? (ts instanceof Date ? ts.toISOString().slice(0, 10) : String(ts).slice(0, 10)) : '';
        return `Attributes for ${label || '—'}`;
    }, [ts]);

    return (
        <OptimizationPipelineConfigurableTable
            data={data}
            title={title}
            searchable
            striped
            emptyText="No data available."
        />
    );
}